/**
 * The sun and the moon: part of the valley, and the way off its screen.
 *
 * Moved out of FullValley so the preview page can draw it, and so anything
 * else that shows the full valley gets the same one. What pressing it DOES is
 * the caller's business -- on the full valley it asks for a PIN.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { loadSheet } from './loader';
import { applySheet, assetUrl } from './sprites';
import { SHEETS } from './art.generated';

/**
 * The sun, and the moon, and the face on them.
 *
 * Ported from the wall display's inline SVG -- the fallback it draws before
 * `celestial-v6.png` loads, which is the right one to take: it needs no
 * asset, scales to any screen, and costs nothing to ship. The sprite sheet
 * stays with the display.
 *
 * A <button>, not the display's <a>, because this goes nowhere on its own --
 * it opens a prompt. Labelled for a screen reader, since to everything except
 * an eye it is a circle.
 *
 * Day and night are NOT a prop. `.is-night` on the wrapper swaps the two orbs
 * in CSS, the way the display does it, so the crossfade is a transition
 * rather than a re-render and the component has nothing to get out of step.
 */
const FACE = [
  { eyes: 'M39 46v2m22-2v2', mouth: 'M44 59Q50 62 56 59' },
  { eyes: 'M39 46v2m22-2v2', mouth: 'M42 60Q50 65 58 60' },
  { eyes: 'M35 48q4-5 8 0m14 0q4-5 8 0', mouth: 'M42 60Q50 70 58 60' },
] as const;

export function Celestial({ balance, onPress }: { balance: number; onPress: () => void }) {
  const open = useRef<HTMLSpanElement>(null);
  const shut = useRef<HTMLSpanElement>(null);
  const [painted, setPainted] = useState(false);

  // The sheet goes onto both frames before the first paint (a layout effect
  // runs synchronously after mount), so there is never a frame of a painted
  // sun with no painting on it.
  useLayoutEffect(() => {
    for (const el of [open.current, shut.current]) if (el) applySheet(el, 'celestial');
  }, []);
  // Ready once the same file the CSS draws is decoded -- AVIF where the
  // browser takes it. (A hidden WebP <img> used to probe this, which fetched
  // the sun twice on every AVIF browser.) First in the art queue: it is the
  // way off the wall.
  useEffect(() => {
    let live = true;
    const file = SHEETS.celestial.file;
    void loadSheet(assetUrl(file, 'avif'), assetUrl(file, 'webp'), 0).then(() => live && setPainted(true), () => {});
    return () => {
      live = false;
    };
  }, []);

  // Its face follows the valley: sad at nothing saved, content while growing,
  // beaming once it is full. The display's thresholds, not the stage's --
  // `renderCelestial` in scene.js uses a fixed $100.
  const mood = balance <= 0 ? 0 : balance < 10_000 ? 1 : 2;
  const columns = SHEETS.celestial.columns;
  const at = (column: number) => `${(column * 100) / (columns - 1)}%`;

  return (
    <button
      type="button"
      className={painted ? 'celestial celestial-light is-painted' : 'celestial celestial-light'}
      onClick={onPress}
      aria-label="Grown-ups: leave the valley"
      title="Grown-ups"
    >
      {/* TWO FRAMES, and the blink only fades the second in and out. An
          earlier version animated background-position between the columns
          through CSS variables inside @keyframes; that resolved to the first
          column -- the sad face -- on Chrome. Opacity is also the cheaper
          thing to animate: it runs on the compositor, where a background
          position repaints every frame, on a screen left on all day. */}
      <span ref={open} className="celestial-art" aria-hidden="true"
            style={{ backgroundPositionX: at(mood * 2) }} />
      <span ref={shut} className="celestial-art celestial-blink" aria-hidden="true"
            style={{ backgroundPositionX: at(mood * 2 + 1) }} />
      <svg aria-hidden="true" viewBox="0 0 100 100">
        <defs>
          <radialGradient id="valley-sun">
            <stop stopColor="#fff4bb" />
            <stop offset="1" stopColor="#e8be67" />
          </radialGradient>
          <radialGradient id="valley-moon">
            <stop stopColor="#f0f0de" />
            <stop offset="1" stopColor="#abbcd1" />
          </radialGradient>
        </defs>
        <circle className="orb sun" cx="50" cy="50" r="34" fill="url(#valley-sun)" />
        <circle className="orb moon" cx="50" cy="50" r="34" fill="url(#valley-moon)" />
        <g className="moon craters" fill="#7b8fa8" opacity=".24">
          <circle cx="34" cy="34" r="5" />
          <circle cx="69" cy="48" r="7" />
          <circle cx="36" cy="67" r="4" />
        </g>
        {/* The same three moods as the painting, in line: `renderWorld` in
            scene.js redraws these two paths by ecology level. The mouth's
            control point is the whole expression -- above the corners is a
            frown, below them a smile. */}
        <g className="face" stroke="#806b43" fill="none" strokeLinecap="round">
          <path d={FACE[mood].eyes} strokeWidth="2.5" />
          <path d={FACE[mood].mouth} strokeWidth="2.3" />
        </g>
      </svg>
    </button>
  );
}
